import { useCallback, useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router';
import { notifyError } from '@/core/notify/notify';
import { NO_PERIOD_PLACEHOLDER, periodRangeText } from '@/core/format/periodLabel';
import { rememberChosenPeriod } from '@/core/periodDefault';
import { selectedClient, type SelectedClient } from '@/core/selectedClient';
import type { PeriodRow } from '@/core/models/practice.models';
import { Select } from '@/kit/Select';

/** Where the Close (X) buttons of these screens go. */
export const PERIODS_ROUTE = '/home/taxreturns/tasks-accountingperiods';

/**
 * The fallback period for a workflow screen (see `resolveDefaultPeriodId` for the one they open on): the latest `periodTo` (compared as text, as Angular did) that is not a
 * comparative period, else the first of that order.
 */
export function currentPeriod(periods: PeriodRow[]): PeriodRow | undefined {
  const sorted = [...periods].sort((a, b) => `${b.periodTo ?? ''}`.localeCompare(`${a.periodTo ?? ''}`));
  return sorted.find((p) => !p.isComparative) ?? sorted[0];
}

/** `new Date(value).toLocaleDateString('en-GB')`, or '' for an empty or invalid value. */
export function displayDate(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  return isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-GB');
}

/** APT355: "from - to"; a date that is missing is left out, and no dates at all (no period yet) is empty so the dropdown's placeholder shows. */
export function periodLabel(period: PeriodRow): string {
  return periodRangeText(displayDate(period?.periodFrom), displayDate(period?.periodTo), ' - ');
}

/** Angular `{{ value | date: 'dd-MMM-yyyy HH:mm' }}` (empty for null). */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  const date = dayjs(value);
  return date.isValid() ? date.format('DD-MMM-YYYY HH:mm') : '';
}

/** Angular `{{ value | date: 'dd-MMM-yyyy' }}` (empty for null). */
export function formatDateOnly(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  const date = dayjs(value);
  return date.isValid() ? date.format('DD-MMM-YYYY') : '';
}

const twoDecimals = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Angular `{{ value | number: '1.2-2' }}` (en-US grouping; empty for null/undefined). */
export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || !isFinite(Number(value))) {
    return '';
  }
  return twoDecimals.format(Number(value));
}

/**
 * The guard every per-client screen ran in `ngOnInit`: without a selected client, toast and go back to Accounts.
 * The client is read once, when the screen opens.
 */
export function useClientGuard(): SelectedClient | null {
  const navigate = useNavigate();
  const [client] = useState(() => selectedClient());
  useEffect(() => {
    if (!client) {
      notifyError('Select a client from Accounts first.');
      void navigate('/home/accountclient');
    }
  }, [client, navigate]);
  return client;
}

/**
 * Angular's `isBusy` flag: `begin()` claims it (false when it is already taken, checked synchronously so a second
 * click in the same tick cannot slip through) and `end()` releases it; `isBusy` drives `[disabled]`.
 */
export function useBusy() {
  const [isBusy, setIsBusy] = useState(false);
  const taken = useRef(false);
  const begin = useCallback(() => {
    if (taken.current) {
      return false;
    }
    taken.current = true;
    setIsBusy(true);
    return true;
  }, []);
  const end = useCallback(() => {
    taken.current = false;
    setIsBusy(false);
  }, []);
  return { isBusy, begin, end };
}

/** Latest value in a ref, for callbacks that outlive the render they were created in (Angular read `this.x` late). */
export function useLatest<T>(value: T) {
  const ref = useRef(value);
  useEffect(() => {
    ref.current = value;
  });
  return ref;
}

/** The "Accounting period" picker of the review points / approval / revisions screens (`bindValue="id"`, labelled by dates). */
export function AccountingPeriodSelect({
  periods,
  value,
  onChange,
}: {
  periods: PeriodRow[];
  value: string | null;
  onChange: (periodId: string) => void;
}) {
  return (
    <Select<PeriodRow>
      className="w-250"
      placeholder={NO_PERIOD_PLACEHOLDER}
      items={periods}
      bindValue="id"
      clearable={false}
      searchable={false}
      value={value || null}
      onChange={(id) => {
        rememberChosenPeriod(id as string);
        onChange(id as string);
      }}
      renderLabel={(item) => periodLabel(item)}
      renderOption={(item) => periodLabel(item)}
    />
  );
}

/** `router.navigate(['home/taxreturns/submitlist'], { queryParams: { id } })`. */
export function submitListPath(id: string): string {
  return `/home/taxreturns/submitlist?id=${encodeURIComponent(id)}`;
}
